import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js'
import { PaymentRequest } from '@stripe/stripe-js'
import { Box } from '@web/components/atoms/Layout'
import { DonationFormStep, useDonationFormContext } from '@web/components/page-components/DonationForm'
import { DonationFrequency, PaymentMethodType } from '@web/graphql-queries/graphql'
import { useEffect, useState } from 'react'
import { usePaymentStore } from '../../../libs/hooks/usePaymentStore'
import { useShallow } from 'zustand/react/shallow'
import { useUpdatePaymentObjectDetailsMutation } from '../../../libs/payment.queries'
import { Skeleton } from '@web/components/atoms/Skeleton'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import { DONOR_MERGE_TAGS } from '@web/components/page-components/Text/MergeTags/utils'
import { useDebugLogging } from 'apps/fe-donation/libs/hooks/useErrorLogging'
import { useTotalAmountWithDonorPays } from './utils'
import Decimal from 'decimal.js'

type QuickPayButtonProps = {
  handlePaymentIntent: (paymentMethodType: PaymentMethodType) => Promise<boolean>
  onReady: () => void
}

const [FIRST_NAME_TAG, LAST_NAME_TAG, EMAIL_TAG] = DONOR_MERGE_TAGS

const QuickPayButton = ({ handlePaymentIntent, onReady }: QuickPayButtonProps) => {
  const stripe = useStripe()
  const clientSecret = usePaymentStore((state) => state.clientSecret)
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const { setStep, validateForm, nextStep } = useDonationFormContext((state) => state.actions)
  const totalAmount = new Decimal(useTotalAmountWithDonorPays()).mul(100).round().toNumber()
  const hasErrors = useDonationFormContext(
    useShallow(({ errors }) => Object.keys(errors).length > 0 || !validateForm()),
  )
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType | null>(null)
  const { mutateAsync: updatePaymentObject } = useUpdatePaymentObjectDetailsMutation()

  const [isStripeButtonReady, setIsStripeButtonReady] = useState(false)
  const { logError } = useDebugLogging()

  const { paymentIntentId, paymentSubscriptionId } = usePaymentStore(({ paymentIntentId, paymentSubscriptionId }) => ({
    paymentIntentId,
    paymentSubscriptionId,
  }))

  useEffect(() => {
    if (!stripe) {
      return
    }

    const pr = stripe.paymentRequest({
      country: 'AU',
      currency: 'aud',
      total: {
        label: 'Donation total',
        amount: isNaN(totalAmount) ? 0 : totalAmount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      disableWallets: ['link'],
    })

    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr)

        if (result.applePay) {
          setPaymentMethodType(PaymentMethodType.ApplePay)
        } else if (result.googlePay) {
          setPaymentMethodType(PaymentMethodType.GooglePay)
        }
      }
    })
  }, [stripe])

  useEffect(() => {
    if (!paymentRequest) {
      return
    }

    paymentRequest.update({ total: { label: 'Donation total', amount: isNaN(totalAmount) ? 0 : totalAmount } })
  }, [totalAmount, paymentRequest])

  useEffect(() => {
    if (!clientSecret || !paymentRequest) {
      return
    }

    paymentRequest.on('paymentmethod', async (ev) => {
      logError('PaymentMethod: Clicked Pay')
      const paymentState = usePaymentStore.getState()
      const res = await updatePaymentObject({
        paymentIntentId: paymentState.paymentIntentId,
        paymentCardType: ev.paymentMethod?.card?.brand ?? 'unknown',
        paymentMethodId: ev.paymentMethod?.id,
        givingFrequency: DonationFrequency.OneTime,
      })
      const { mergeFields, setMergeField } = useMergeFields.getState()
      if (!mergeFields.get(EMAIL_TAG.MergeFieldSettingId)) {
        setMergeField(EMAIL_TAG.MergeFieldSettingId, ev.payerEmail ?? '')
      }

      if (!mergeFields.get(FIRST_NAME_TAG.MergeFieldSettingId)) {
        const [firstName, lastName] = ev.payerName?.split(' ') ?? []
        setMergeField(FIRST_NAME_TAG.MergeFieldSettingId, firstName)
        setMergeField(LAST_NAME_TAG.MergeFieldSettingId, lastName)
      }
      // Confirm the PaymentIntent without handling potential next actions (yet).
      const { paymentIntent, error: confirmError } = await stripe!.confirmCardPayment(
        res.clientSecret ?? paymentState.clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      )

      logError('PaymentMethod: Confirmed Card')

      if (confirmError) {
        // Report to the browser that the payment failed, prompting it to
        // re-show the payment interface, or show an error message and close
        // the payment interface.
        if (confirmError.type === 'card_error') {
          usePaymentStore.setState({ paymentError: confirmError.message })
        }
        setStep(DonationFormStep.ErrorStep)
        logError('PaymentMethod: Failed to Confirm Card ' + confirmError.message)
        ev.complete('fail')
      } else {
        // Report to the browser that the confirmation was successful, prompting
        // it to close the browser payment method collection interface.
        ev.complete('success')
        // Check if the PaymentIntent requires any actions and, if so, let Stripe.js
        // handle the flow. If using an API version older than "2019-02-11"
        // instead check for: `paymentIntent.status === "requires_source_action"`.
        if (paymentIntent.status === 'requires_action') {
          // Let Stripe.js handle the rest of the payment flow.
          logError('PaymentIntent: Requires Action')
          const { error } = await stripe!.confirmCardPayment(clientSecret)
          if (error) {
            // The payment failed -- ask your customer for a new payment method.
            logError('PaymentIntent: Failed to Confirm Card After Action ' + error)
            setStep(DonationFormStep.ErrorStep)
          } else {
            // The payment has succeeded -- show a success message to your customer.
            logError('PaymentIntent: Success after Action')
            nextStep()
          }
        } else {
          // The payment has succeeded -- show a success message to your customer.
          logError('PaymentIntent: Success')
          nextStep()
        }
      }
    })

    paymentRequest.on('cancel', () => {
      logError('PaymentMethod: Cancelled')
    })
  }, [clientSecret, paymentRequest])

  return paymentRequest ? (
    <Box w="100%">
      {!isStripeButtonReady && <Skeleton width="100%" height="50px" />}
      <PaymentRequestButtonElement
        onReady={async () => {
          setIsStripeButtonReady(true)
          if (await paymentRequest?.canMakePayment()) {
            onReady()
          }
        }}
        onClick={async (e) => {
          if (hasErrors) {
            e.preventDefault()
            validateForm(true)
            return
          }
          logError('Payment Request Clicked')

          if (!paymentMethodType || !(await handlePaymentIntent(paymentMethodType))) {
            paymentRequest.abort()

            return
          }

          logError('Payment Request Pop Up')
        }}
        options={{ paymentRequest, style: { paymentRequestButton: { height: '50px' } } }}
      />
    </Box>
  ) : null
}

export default QuickPayButton
